import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/backendUrl';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../components/Toast';
import { ordersAPI } from '../lib/api';
import { 
  ShoppingBag, 
  Clock, 
  CheckCircle, 
  Truck, 
  Package,
  XCircle,
  RefreshCw,
  MapPin,
  Phone,
  CreditCard,
  MessageSquare,
  Eye,
  X,
  User,
  Calendar,
  FileText,
  DollarSign,
  Receipt
} from 'lucide-react';

const statusConfig = {
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmado: { label: 'Confirmado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  preparando: { label: 'Preparando', color: 'bg-purple-100 text-purple-800', icon: Package },
  entregando: { label: 'Em Entrega', color: 'bg-orange-100 text-orange-800', icon: Truck },
  entregue: { label: 'Entregue', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
  // Fallback para inglês (caso antigo)
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmed: { label: 'Confirmado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  preparing: { label: 'Preparando', color: 'bg-purple-100 text-purple-800', icon: Package },
  delivering: { label: 'Em Entrega', color: 'bg-orange-100 text-orange-800', icon: Truck },
  delivered: { label: 'Entregue', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function WhatsAppOrdersPage() {
  const toast = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [previousOrderCount, setPreviousOrderCount] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showEmitirNotaModal, setShowEmitirNotaModal] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState('nfce');
  const [emitindoNota, setEmitindoNota] = useState(false);

  // Som de notificação para novo pedido (Web Audio API — perceptível)
  const playNotificationSound = () => {
    if (!soundEnabled) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const playTone = (freq, startAt, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, ctx.currentTime + startAt);
        gain.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + startAt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startAt + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(ctx.currentTime + startAt);
        osc.stop(ctx.currentTime + startAt + duration + 0.05);
      };
      playTone(880, 0.0, 0.18);
      playTone(1320, 0.18, 0.22);
      playTone(1760, 0.42, 0.28);
      setTimeout(() => { try { ctx.close(); } catch (e) {} }, 1200);
    } catch (e) {}
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getWhatsAppOrders(filter === 'all' ? null : filter);
      const newOrders = response.data.orders || response.data || [];
      
      // Verificar se há novos pedidos
      if (newOrders.length > previousOrderCount && previousOrderCount > 0) {
        const newCount = newOrders.length - previousOrderCount;
        playNotificationSound();
        toast.success(`🔔 ${newCount} novo(s) pedido(s)!`);
      }
      
      setPreviousOrderCount(newOrders.length);
      setOrders(newOrders);
    } catch (error) {
      console.error('Erro ao carregar pedidos:', error);
      toast.error('Erro ao carregar pedidos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    // Auto-refresh a cada 15 segundos para detectar novos pedidos
    const interval = setInterval(loadOrders, 15000);
    return () => clearInterval(interval);
  }, [filter]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrder(orderId);
      await ordersAPI.updateWhatsAppOrderStatus(orderId, newStatus);
      
      // Se marcou como entregue, gerar fluxo de caixa
      if (newStatus === 'entregue') {
        const order = orders.find(o => o.id === orderId);
        if (order) {
          try {
            await ordersAPI.generateCashFlowFromOrder(orderId);
            toast.success(`✅ Pedido entregue e registrado no fluxo de caixa!`);
          } catch (e) {
            toast.success(`Status atualizado para: ${statusConfig[newStatus]?.label}`);
            console.error('Erro ao gerar fluxo de caixa:', e);
          }
        }
      } else {
        toast.success(`Status atualizado para: ${statusConfig[newStatus]?.label}`);
      }
      
      loadOrders();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdatingOrder(null);
    }
  };

  // Função para emitir nota fiscal do pedido
  const emitirNotaFiscal = async (order) => {
    if (!order) return;
    
    setEmitindoNota(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/orders/${order.id}/emitir-nota`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          tipo_documento: tipoDocumento
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.sucesso) {
        const tipoNota = tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e';
        toast.success(`${tipoNota} emitida com sucesso! Chave: ${data.chave?.substring(0, 20)}...`);
        setShowEmitirNotaModal(false);
        loadOrders();
      } else {
        toast.error(data.detail || data.message || 'Erro ao emitir nota fiscal');
      }
    } catch (error) {
      toast.error('Erro ao emitir nota fiscal');
    } finally {
      setEmitindoNota(false);
    }
  };

  const getNextStatus = (currentStatus) => {
    const flow = {
      pendente: 'confirmado',
      confirmado: 'preparando',
      preparando: 'entregando',
      entregando: 'entregue',
      // Fallback inglês
      pending: 'confirmado',
      confirmed: 'preparando',
      preparing: 'entregando',
      delivering: 'entregue'
    };
    return flow[currentStatus];
  };

  const pendingCount = orders.filter(o => o.status === 'pendente' || o.status === 'pending').length;
  const preparingCount = orders.filter(o => ['confirmado', 'preparando', 'confirmed', 'preparing'].includes(o.status)).length;
  const deliveringCount = orders.filter(o => o.status === 'entregando' || o.status === 'delivering').length;
  const deliveredCount = orders.filter(o => o.status === 'entregue' || o.status === 'delivered').length;

  // Calcular totais - extrai valor do campo items_description ou total
  const extractTotal = (order) => {
    if (order.total) {
      const match = String(order.total).match(/[\d,.]+/);
      return match ? parseFloat(match[0].replace(',', '.')) : 0;
    }
    if (order.items_description) {
      const match = order.items_description.match(/R\$\s*([\d,.]+)/g);
      if (match) {
        return match.reduce((sum, val) => {
          const num = parseFloat(val.replace('R$', '').replace(',', '.').trim());
          return sum + (isNaN(num) ? 0 : num);
        }, 0);
      }
    }
    return 0;
  };

  const totalPendente = orders.filter(o => o.status === 'pendente' || o.status === 'pending').reduce((sum, o) => sum + extractTotal(o), 0);
  const totalEntregue = orders.filter(o => o.status === 'entregue' || o.status === 'delivered').reduce((sum, o) => sum + extractTotal(o), 0);
  const totalGeral = orders.reduce((sum, o) => sum + extractTotal(o), 0);

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetails(true);
  };

  const closeOrderDetails = () => {
    setShowDetails(false);
    setSelectedOrder(null);
  };

  // Finalizar pedido e lançar no caixa
  const [finalizando, setFinalizando] = useState(null);
  
  const finalizarPedido = async (order) => {
    if (!order) return;
    
    setFinalizando(order.id);
    try {
      const response = await fetch(`${BACKEND_URL}/api/whatsapp/orders/${order.id}/finalize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(`✅ Pedido finalizado! R$ ${data.amount?.toFixed(2) || '0,00'} registrado no caixa`);
        loadOrders();
      } else {
        toast.error(data.detail || data.message || 'Erro ao finalizar pedido');
      }
    } catch (error) {
      toast.error('Erro ao finalizar pedido');
    } finally {
      setFinalizando(null);
    }
  };

  // Modal de Detalhes do Pedido
  const OrderDetailsModal = ({ order, onClose }) => {
    if (!order) return null;
    
    const status = order.status || 'pendente';
    const StatusIcon = statusConfig[status]?.icon || Clock;
    const statusColor = statusConfig[status]?.color || 'bg-gray-100 text-gray-800';
    const statusLabel = statusConfig[status]?.label || status;
    const nextStatus = getNextStatus(status);

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                Pedido #{order.id?.substring(0, 8).toUpperCase()}
              </h2>
              <p className="text-sm text-slate-500">{formatDate(order.created_at)}</p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={statusColor}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusLabel}
              </Badge>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Informações do Cliente */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Informações do Cliente
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Nome</p>
                  <p className="font-medium">{order.customer_name || 'Não informado'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Telefone</p>
                  <p className="font-medium flex items-center gap-1">
                    <Phone className="w-4 h-4 text-green-600" />
                    {order.customer_phone || 'Não informado'}
                  </p>
                </div>
                {order.delivery && order.delivery_address && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-slate-500">Endereço de Entrega</p>
                    <p className="font-medium flex items-center gap-1">
                      <MapPin className="w-4 h-4 text-red-500" />
                      {order.delivery_address}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Itens do Pedido */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4" /> Itens do Pedido
              </h3>
              
              {order.items && order.items.length > 0 ? (
                <div className="space-y-2">
                  {order.items.map((item, idx) => (
                    <div key={item.product_id || `${item.product_name || item.name || ''}-${idx}`} className="flex justify-between items-center py-2 border-b border-slate-200 last:border-0">
                      <div>
                        <p className="font-medium">{item.product_name || item.name}</p>
                        <p className="text-sm text-slate-500">
                          {item.quantity}x {formatCurrency(item.unit_price || item.price || 0)}
                        </p>
                      </div>
                      <p className="font-semibold text-emerald-600">
                        {formatCurrency(item.total || (item.quantity * (item.unit_price || item.price || 0)))}
                      </p>
                    </div>
                  ))}
                </div>
              ) : order.items_description ? (
                <div className="bg-white rounded p-3 border">
                  <p className="text-sm whitespace-pre-wrap">{order.items_description}</p>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">Sem itens detalhados</p>
              )}
            </div>

            {/* Pagamento e Totais */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Pagamento
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600">Forma de Pagamento</span>
                  <span className="font-medium flex items-center gap-1">
                    <CreditCard className="w-4 h-4" />
                    {order.payment_method?.charAt(0).toUpperCase() + order.payment_method?.slice(1) || 'Não informado'}
                  </span>
                </div>
                {order.delivery_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Taxa de Entrega</span>
                    <span className="font-medium">{formatCurrency(order.delivery_fee)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-slate-200">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-xl font-bold text-emerald-600">
                    {order.total || formatCurrency(extractTotal(order))}
                  </span>
                </div>
              </div>
            </div>

            {/* Observações */}
            {order.notes && (
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h3 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Observações
                </h3>
                <p className="text-yellow-900">{order.notes}</p>
              </div>
            )}

            {/* Timeline do Pedido */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Histórico
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span>Pedido criado em {formatDate(order.created_at)}</span>
                </div>
                {order.updated_at && order.updated_at !== order.created_at && (
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span>Última atualização em {formatDate(order.updated_at)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer com Ações */}
          <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-between items-center">
            <Button variant="outline" onClick={onClose}>
              Fechar
            </Button>
            <div className="flex gap-2">
              {nextStatus && order.status !== 'cancelled' && order.status !== 'entregue' && (
                <Button
                  onClick={() => {
                    updateStatus(order.id, nextStatus);
                    onClose();
                  }}
                  disabled={updatingOrder === order.id}
                >
                  {updatingOrder === order.id ? 'Atualizando...' : `Avançar para ${statusConfig[nextStatus]?.label}`}
                </Button>
              )}
              {order.status !== 'cancelado' && order.status !== 'cancelled' && order.status !== 'entregue' && order.status !== 'delivered' && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    updateStatus(order.id, 'cancelado');
                    onClose();
                  }}
                  disabled={updatingOrder === order.id}
                >
                  Cancelar Pedido
                </Button>
              )}
              {/* Botão Emitir Nota */}
              {(order.status === 'confirmado' || order.status === 'entregue' || order.status === 'delivered') && !order.nfce_key && !order.nfe_key && (
                <Button
                  variant="outline"
                  className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                  onClick={() => {
                    setSelectedOrder(order);
                    setShowEmitirNotaModal(true);
                  }}
                >
                  <Receipt className="w-4 h-4 mr-2" />
                  Emitir Nota
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Modal para escolher tipo de documento
  const EmitirNotaModal = () => {
    if (!showEmitirNotaModal || !selectedOrder) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowEmitirNotaModal(false)}>
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-xl font-bold text-slate-900 mb-4">Emitir Documento Fiscal</h2>
          <p className="text-slate-600 mb-6">Pedido #{selectedOrder.id?.substring(0, 8).toUpperCase()}</p>
          
          <div className="space-y-3 mb-6">
            <button
              type="button"
              onClick={() => setTipoDocumento('nfce')}
              className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                tipoDocumento === 'nfce' 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <span className="font-semibold block">NFC-e - Nota Fiscal do Consumidor</span>
              <span className="text-sm text-slate-500">Para vendas a consumidor final (pessoa física)</span>
            </button>
            <button
              type="button"
              onClick={() => setTipoDocumento('nfe')}
              className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                tipoDocumento === 'nfe' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <span className="font-semibold block">NF-e - Nota Fiscal Eletrônica</span>
              <span className="text-sm text-slate-500">Para vendas entre empresas (B2B)</span>
            </button>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowEmitirNotaModal(false)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={() => emitirNotaFiscal(selectedOrder)} 
              disabled={emitindoNota}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            >
              {emitindoNota ? 'Emitindo...' : `Emitir ${tipoDocumento === 'nfe' ? 'NF-e' : 'NFC-e'}`}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-orders-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pedidos WhatsApp</h1>
          <p className="text-slate-600">Gerencie os pedidos recebidos pelo WhatsApp</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => setSoundEnabled(!soundEnabled)} 
            variant="outline"
            size="sm"
            className={soundEnabled ? 'bg-green-50' : 'bg-gray-50'}
          >
            {soundEnabled ? '🔔 Som Ativado' : '🔕 Som Desativado'}
          </Button>
          <Button onClick={loadOrders} disabled={loading} variant="outline">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-600 text-sm font-medium">Pendentes</p>
                <p className="text-2xl font-bold text-yellow-700">{pendingCount}</p>
                <p className="text-xs text-yellow-600">R$ {totalPendente.toFixed(2)}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium">Em Preparo</p>
                <p className="text-2xl font-bold text-purple-700">{preparingCount}</p>
              </div>
              <Package className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium">Em Entrega</p>
                <p className="text-2xl font-bold text-orange-700">{deliveringCount}</p>
              </div>
              <Truck className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium">Entregues</p>
                <p className="text-2xl font-bold text-green-700">{deliveredCount}</p>
                <p className="text-xs text-green-600">R$ {totalEntregue.toFixed(2)}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium">Total Geral</p>
                <p className="text-2xl font-bold text-blue-700">{orders.length} pedidos</p>
                <p className="text-xs text-blue-600">R$ {totalGeral.toFixed(2)}</p>
              </div>
              <ShoppingBag className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pendente', 'confirmado', 'preparando', 'entregando', 'entregue', 'cancelado'].map(status => (
          <Button
            key={status}
            variant={filter === status ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(status)}
            data-testid={`filter-${status}`}
          >
            {status === 'all' ? 'Todos' : statusConfig[status]?.label || status}
          </Button>
        ))}
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {loading ? (
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              Carregando pedidos...
            </CardContent>
          </Card>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-slate-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum pedido encontrado</p>
              <p className="text-sm mt-2">Os pedidos do WhatsApp aparecerão aqui</p>
            </CardContent>
          </Card>
        ) : (
          orders.map(order => {
            const status = order.status || 'pendente';
            const StatusIcon = statusConfig[status]?.icon || Clock;
            const nextStatus = getNextStatus(status);
            const statusColor = statusConfig[status]?.color || 'bg-gray-100 text-gray-800';
            const statusLabel = statusConfig[status]?.label || status;
            
            return (
              <Card key={order.id} className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`order-${order.id}`} onClick={() => openOrderDetails(order)}>
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        Pedido #{order.id?.substring(0, 8).toUpperCase()}
                        {order.delivery && (
                          <Badge variant="outline" className="text-xs">
                            <Truck className="w-3 h-3 mr-1" /> Entrega
                          </Badge>
                        )}
                      </CardTitle>
                      <p className="text-sm text-slate-500">{formatDate(order.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={statusColor}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusLabel}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openOrderDetails(order); }}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Cliente */}
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm text-slate-700">Cliente</h4>
                      <p className="text-sm">{order.customer_name || 'Não informado'}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {order.customer_phone}
                      </p>
                    </div>
                    
                    {/* Itens Resumo */}
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm text-slate-700">Itens</h4>
                      <p className="text-sm text-slate-600">
                        {order.items?.length > 0 
                          ? `${order.items.length} item(ns)` 
                          : order.items_description?.substring(0, 50) + '...' || 'Ver detalhes'}
                      </p>
                    </div>
                    
                    {/* Total e Ações Fiscais */}
                    <div className="space-y-2">
                      <h4 className="font-semibold text-sm text-slate-700">Total</h4>
                      <p className="text-lg font-bold text-emerald-600">
                        {order.total || formatCurrency(extractTotal(order))}
                      </p>
                      <p className="text-xs text-slate-500">
                        {order.payment_method?.charAt(0).toUpperCase() + order.payment_method?.slice(1) || ''}
                      </p>
                      
                      {/* Botões de Nota Fiscal */}
                      {!order.nfce_key && !order.nfe_key ? (
                        <div className="flex gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrder(order);
                              setTipoDocumento('nfce');
                              setShowEmitirNotaModal(true);
                            }}
                          >
                            <Receipt className="w-3 h-3 mr-1" />
                            NFC-e
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedOrder(order);
                              setTipoDocumento('nfe');
                              setShowEmitirNotaModal(true);
                            }}
                          >
                            <FileText className="w-3 h-3 mr-1" />
                            NF-e
                          </Button>
                        </div>
                      ) : (
                        <Badge className="mt-2 bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {order.tipo_documento === 'nfe' ? 'NF-e' : 'NFC-e'} Emitida
                        </Badge>
                      )}
                      
                      {/* Botão Finalizar e Lançar no Caixa */}
                      {!order.cashflow_generated && (order.status === 'entregue' || order.status === 'delivered' || order.status === 'finalizado') && (
                        <Button
                          size="sm"
                          className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            finalizarPedido(order);
                          }}
                          disabled={finalizando === order.id}
                        >
                          {finalizando === order.id ? (
                            <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <DollarSign className="w-3 h-3 mr-1" />
                          )}
                          Finalizar e Lançar no Caixa
                        </Button>
                      )}
                      {order.cashflow_generated && (
                        <Badge className="mt-2 bg-blue-100 text-blue-700">
                          <DollarSign className="w-3 h-3 mr-1" />
                          Lançado no Caixa
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Modal de Detalhes */}
      {showDetails && selectedOrder && (
        <OrderDetailsModal order={selectedOrder} onClose={closeOrderDetails} />
      )}
      
      {/* Modal Emitir Nota */}
      <EmitirNotaModal />
    </div>
  );
}
